import React, { useState } from 'react';
import { XIcon, ShoppingBagIcon, EditIcon, Trash2Icon, PackageIcon, MinusIcon, PlusIcon } from 'lucide-react';
export interface ShoppingBagItem {
  id: string;
  name: string;
  productId: string;
  productName: string;
  colorId: string;
  colorName: string;
  designs: {
    front: any[];
    back: any[];
  };
  sizeQuantities: {
    [size: string]: number;
  };
  thumbnail: string;
  createdAt: Date;
}
interface ShoppingBagProps {
  isOpen: boolean;
  onClose: () => void;
  items: ShoppingBagItem[];
  onEditItem: (itemId: string) => void;
  onRemoveItem: (itemId: string) => void;
  onUpdateSizeQuantities: (itemId: string, sizeQuantities: {
    [size: string]: number;
  }) => void;
  onProceedToCheckout: () => void;
}
const AVAILABLE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const BASE_PRICE = 29.99; // Price per item
export function ShoppingBag({
  isOpen,
  onClose,
  items,
  onEditItem,
  onRemoveItem,
  onUpdateSizeQuantities,
  onProceedToCheckout
}: ShoppingBagProps) {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  if (!isOpen) return null;
  const updateQuantity = (itemId: string, size: string, quantity: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const newSizeQuantities = {
      ...item.sizeQuantities
    };
    if (quantity <= 0) {
      delete newSizeQuantities[size];
    } else {
      newSizeQuantities[size] = Math.min(99, Math.max(1, quantity));
    }
    onUpdateSizeQuantities(itemId, newSizeQuantities);
  };
  const incrementQuantity = (itemId: string, size: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const currentQty = item.sizeQuantities[size] || 0;
    updateQuantity(itemId, size, currentQty + 1);
  };
  const decrementQuantity = (itemId: string, size: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const currentQty = item.sizeQuantities[size] || 0;
    if (currentQty > 0) {
      updateQuantity(itemId, size, currentQty - 1);
    }
  };
  const totalItems = items.reduce((sum, item) => {
    return sum + Object.values(item.sizeQuantities).reduce((s, q) => s + q, 0);
  }, 0);
  const totalPrice = totalItems * BASE_PRICE;
  const hasAllQuantities = items.every(item => Object.keys(item.sizeQuantities).length > 0 && Object.values(item.sizeQuantities).every(q => q > 0));
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center" data-id="element-380">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-2xl sm:max-h-[80vh] flex flex-col" data-id="element-381">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-382">
          <div className="flex items-center gap-3" data-id="element-383">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-384">
              <ShoppingBagIcon className="w-5 h-5 text-blue-600" data-id="element-385" />
            </div>
            <div data-id="element-386">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-387">
                Shopping Bag
              </h3>
              <p className="text-sm text-gray-500" data-id="element-388">
                {items.length} design{items.length !== 1 ? 's' : ''} ·{' '}
                {totalItems} item{totalItems !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-389">
            <XIcon className="w-5 h-5" data-id="element-390" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-391">
          {items.length === 0 ? <div className="text-center py-12" data-id="element-392">
              <PackageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-393" />
              <p className="text-gray-500 text-sm" data-id="element-394">
                Your shopping bag is empty
              </p>
              <p className="text-gray-400 text-xs mt-1" data-id="element-395">
                Design something awesome and add it here!
              </p>
            </div> : <div className="space-y-4" data-id="element-396">
              {items.map(item => {
            const isExpanded = expandedItemId === item.id;
            const itemTotal = Object.values(item.sizeQuantities).reduce((s, q) => s + q, 0);
            return <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-397">
                    {/* Item Header */}
                    <div className="p-4" data-id="element-398">
                      <div className="flex gap-4" data-id="element-399">
                        <img src={item.thumbnail} alt={item.name} className="w-20 h-20 object-cover rounded-lg bg-gray-50 border border-gray-200" data-id="element-400" />
                        <div className="flex-1 min-w-0" data-id="element-401">
                          <h4 className="text-sm font-semibold text-gray-900 truncate" data-id="element-402">
                            {item.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-403">
                            {item.colorName} {item.productName}
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-404">
                            ${BASE_PRICE.toFixed(2)} per item
                          </p>
                          <p className="text-sm font-semibold text-gray-900 mt-1" data-id="element-405">
                            {itemTotal > 0 ? <span className="text-blue-600" data-id="element-406">
                                {itemTotal} item{itemTotal !== 1 ? 's' : ''} · $
                                {(itemTotal * BASE_PRICE).toFixed(2)}
                              </span> : <span className="text-red-600" data-id="element-407">
                                No sizes/quantities selected
                              </span>}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2" data-id="element-408">
                          <button onClick={() => onEditItem(item.id)} className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit design" data-id="element-409">
                            <EditIcon className="w-4 h-4" data-id="element-410" />
                          </button>
                          <button onClick={() => onRemoveItem(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors" title="Remove from bag" data-id="element-411">
                            <Trash2Icon className="w-4 h-4" data-id="element-412" />
                          </button>
                        </div>
                      </div>

                      {/* Size/Quantity Selection Toggle */}
                      <button onClick={() => setExpandedItemId(isExpanded ? null : item.id)} className="w-full mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors" data-id="element-413">
                        {isExpanded ? 'Hide' : 'Select'} Sizes & Quantities
                      </button>
                    </div>

                    {/* Size & Quantity Selection (Expanded) */}
                    {isExpanded && <div className="border-t border-gray-200 bg-gray-50 p-4" data-id="element-414">
                        <p className="text-xs font-medium text-gray-700 mb-3" data-id="element-415">
                          Select sizes and quantities:
                        </p>
                        <div className="space-y-2" data-id="element-416">
                          {AVAILABLE_SIZES.map(size => {
                    const quantity = item.sizeQuantities[size] || 0;
                    const isSelected = quantity > 0;
                    return <div key={size} className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200'}`} data-id="element-417">
                                <span className="text-sm font-medium text-gray-900 w-12" data-id="element-418">
                                  {size}
                                </span>

                                <div className="flex items-center gap-3" data-id="element-419">
                                  <button onClick={() => decrementQuantity(item.id, size)} disabled={quantity === 0} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors" data-id="element-420">
                                    <MinusIcon className="w-4 h-4 text-gray-700" data-id="element-421" />
                                  </button>

                                  <input type="number" min="0" max="99" value={quantity} onChange={e => updateQuantity(item.id, size, parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-422" />

                                  <button onClick={() => incrementQuantity(item.id, size)} disabled={quantity >= 99} className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-423">
                                    <PlusIcon className="w-4 h-4 text-white" data-id="element-424" />
                                  </button>

                                  <span className="text-sm text-gray-600 w-20 text-right" data-id="element-425">
                                    ${(quantity * BASE_PRICE).toFixed(2)}
                                  </span>
                                </div>
                              </div>;
                  })}
                        </div>

                        {itemTotal > 0 && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-426">
                            <p className="text-sm text-gray-600" data-id="element-427">
                              Selected:{' '}
                              {Object.entries(item.sizeQuantities).filter(([_, qty]) => qty > 0).map(([size, qty]) => `${size} (${qty})`).join(', ')}
                            </p>
                          </div>}
                      </div>}
                  </div>;
          })}
            </div>}
        </div>

        {/* Footer */}
        {items.length > 0 && <div className="border-t border-gray-200 p-6 bg-gray-50" data-id="element-428">
            <div className="space-y-2 mb-4" data-id="element-429">
              <div className="flex items-center justify-between text-sm" data-id="element-430">
                <span className="text-gray-600" data-id="element-431">Total Items:</span>
                <span className="font-semibold text-gray-900" data-id="element-432">
                  {totalItems}
                </span>
              </div>
              <div className="flex items-center justify-between text-lg" data-id="element-433">
                <span className="font-semibold text-gray-900" data-id="element-434">Subtotal:</span>
                <span className="font-bold text-gray-900" data-id="element-435">
                  ${totalPrice.toFixed(2)}
                </span>
              </div>
            </div>

            <button onClick={onProceedToCheckout} disabled={!hasAllQuantities || totalItems === 0} className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors" data-id="element-436">
              Proceed to Checkout
            </button>

            {!hasAllQuantities && totalItems === 0 && <p className="text-xs text-red-600 text-center mt-2" data-id="element-437">
                Please select sizes and quantities for all designs
              </p>}
          </div>}
      </div>
    </div>;
}